import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  RevenueRecognitionReportItem,
  ReportItem,
} from "types/Models/consolidatedReport";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import {
  REVENUE_SCHEDULE_EXPORT_TYPES,
  REVENUE_SCHEDULE_EXPORT_TYPES_DISPLAY,
} from "constants/revenueScheduleExportTypes";
import { getCurrentDisplayValue } from "components/DesignSystem/Combobox/utils/getCurrentDisplayValue";
import { FormikForm } from "components/FormikForm/FormikForm";
import { getUsedGroupBysForReport } from "utils/allReports";

type RevenueRecognitionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: RevenueRecognitionReportItem | null;
  onSave: (data: {
    groupBy: string;
    groupByLabel: string;
    includeCarryForward: boolean;
    showDeferrals: boolean;
  }) => void;
  modalTitle: string;
  submitText: string;
  selectedReports?: ReportItem[];
};

type FormValues = {
  groupBy: string;
  groupByLabel: string;
  includeCarryForward: boolean;
  showDeferrals: boolean;
};

export const RevenueRecognitionModal = ({
  isOpen,
  onClose,
  report,
  onSave,
  modalTitle,
  submitText,
  selectedReports = [],
}: RevenueRecognitionModalProps) => {
  const initialValues: FormValues = {
    groupBy: report?.groupBy || "Month",
    groupByLabel: report?.groupByLabel || "Months",
    includeCarryForward: report?.includeCarryForward || false,
    showDeferrals: report?.showDeferrals || false,
  };

  const usedGroupBys = report
    ? getUsedGroupBysForReport(report.value, selectedReports)
    : [];

  const groupByOptions = convertDisplayToOptions(
    REVENUE_SCHEDULE_EXPORT_TYPES_DISPLAY
  ).map((opt) => ({
    ...opt,
    disableOption: usedGroupBys.includes(opt.value),
    data: usedGroupBys.includes(opt.value) ? "Added already" : undefined,
  }));

  const handleSubmit = (values: FormValues) => {
    onSave(values);
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular" asChild>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title>{modalTitle}</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-4">
                  <div>
                    <Combobox
                      required
                      withForm
                      onChange={(option) => {
                        if (option instanceof Array) {
                          return;
                        }
                        if (
                          option?.value ===
                          REVENUE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS
                        ) {
                          setFieldValue("includeCarryForward", false);
                        }
                        setFieldValue("groupByLabel", String(option?.label));
                      }}
                      name="groupBy"
                      label="Group by"
                      options={groupByOptions}
                      value={getCurrentDisplayValue({
                        value: values.groupBy,
                        options: groupByOptions,
                      })}
                      menuPortalTarget={document.body}
                      isClearable={false}
                      tooltip
                      isOptionDisabled={(option) =>
                        Boolean(option.disableOption)
                      }
                    />
                  </div>

                  <div className="t-flex t-flex-col t-gap-3">
                    {values.groupBy !==
                      REVENUE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS && (
                      <Checkbox
                        name="includeCarryForward"
                        checked={values.includeCarryForward}
                        onChange={(e) => {
                          setFieldValue(
                            "includeCarryForward",
                            e.target.checked
                          );
                        }}
                        label="Include carry forward from prior month"
                      />
                    )}

                    <Checkbox
                      name="showDeferrals"
                      checked={values.showDeferrals}
                      onChange={(e) => {
                        setFieldValue("showDeferrals", e.target.checked);
                      }}
                      label="Include deferrals"
                    />
                  </div>
                </div>
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Button customType="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button customType="primary" type="submit">
                  {submitText}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
